'use client'

import Image from 'next/image'
import classNames from 'classnames'
import { useWebviewStore } from '@/store/webview'
import { Icon } from '../Icon'
import VWorldIcon from '@/public/images/virtualworld.png'
import { useEffect, useLayoutEffect, useMemo, useState } from 'react'
import { STORAGE_CONSTANTS } from '@/util/storage'
import Link from 'next/link'
import useSWR from 'swr'
import { AppApi } from '@/api/app'
import { useAuthStore } from '@/store/auth'
import { createT } from '@/locales/index'
import { LanguageCode } from '@/api/types/language'

type Props = {
  children?: React.ReactNode
}

const wrapCode = (code: string) => {
  if (code === 'ja') {
    return 'jp' as LanguageCode
  }

  return code as LanguageCode
}

export const BaseHeader = ({ children }: Props) => {
  const { user } = useAuthStore()
  const webview = useWebviewStore()
  const [isMobile, setIsMobile] = useState(false)
  const [os, setOs] = useState('')
  const [language, setLanguage] = useState(user?.language)
  const [bannerClosed, setBannerClose] = useState(true)
  const { data: translation, isLoading } = useSWR(
    ['header-keyword', language],
    (args) =>
      AppApi().keywords(wrapCode(args[1] || navigator.language.split('-')[0])),
    {
      keepPreviousData: true,
    },
  )

  const t = createT(translation)

  const showAppDownload =
    !webview.isNative && isMobile && !bannerClosed && !isLoading

  const closeBanner = () => {
    localStorage.setItem(STORAGE_CONSTANTS.APP_DOWNLOAD_BANNER_CLOSE, '1')
    setBannerClose(true)
  }

  useEffect(() => {
    const ua = window.navigator.userAgent.toLowerCase()
    setIsMobile(/iphone|ipad|ipod|android/i.test(ua))
    if (/iphone|ipad|ipod/i.test(ua)) {
      setOs('ios')
    }
    if (/android/i.test(ua)) {
      setOs('android')
    }

    if (!localStorage.getItem(STORAGE_CONSTANTS.APP_DOWNLOAD_BANNER_CLOSE)) {
      setBannerClose(false)
    }
  }, [])

  useEffect(() => {
    if (!user) {
      setLanguage(navigator.language.split('-')[0] as LanguageCode)
    } else {
      setLanguage(user.language)
    }
  }, [user])

  useLayoutEffect(() => {
    const setNavMargin = () => {
      if (window.innerWidth >= 900) {
        document.documentElement.style.setProperty('--header-height', '4.25rem')
      } else {
        document.documentElement.style.setProperty(
          '--header-height',
          showAppDownload ? 'calc(75px + 3rem)' : '3rem',
        )
      }
    }

    setNavMargin()

    window.addEventListener('resize', setNavMargin)

    return () => {
      window.removeEventListener('resize', setNavMargin)
    }
  }, [showAppDownload])

  const appdownloadLink = useMemo(() => {
    if (os === 'android') {
      return 'https://play.google.com/store/apps/details?id=kr.co.buv.virtual'
    }

    if (os === 'ios') {
      return 'https://apps.apple.com/app/id6466983132'
    }

    return ''
  }, [os])

  return (
    <nav
      className={classNames(
        'pc:hidden h-12 -mt-6 transition-all pc:border-b w-full border-gray-100 shrink-0 bg-background z-50 left-0 top-0',
        {
          '!h-[calc(75px+3rem)]': showAppDownload,
        },
      )}
    >
      <div className="fixed bg-background top-0 left-0 w-full z-50">
        {showAppDownload && (
          <div className="h-[75px] border-b border-gray-100 flex items-center gap-2.5 px-4">
            <Icon
              name="close"
              size={24}
              className="!text-text-60"
              onClick={closeBanner}
            />
            <Image src={VWorldIcon} alt="V-World" className="w-11" />
            <div
              className="text-body2 font-medium flex-1 whitespace-pre-line"
              dangerouslySetInnerHTML={{ __html: t('app-header-download') }}
            />
            <Link
              href={appdownloadLink}
              target="_blank"
              className="bg-key rounded px-2 py-1.5 text-caption2 font-bold"
            >
              {t('download-app')}
            </Link>
          </div>
        )}
        <div className="max-w-layout mx-auto h-12 flex items-center justify-between px-4 gap-4 relative">
          {children}
        </div>
      </div>
    </nav>
  )
}
